import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './FlashCard.css';
import ChanterSvg from './ChanterSvg';
import NoteStaff from './NoteStaff';
import SubMenuBar from './SubMenuBar';
import { getItem, setItem } from '../utils/storage';
import { trackFeatureUsage, trackError } from '../utils/analytics';
import keyboardChanter from '../utils/keyboardChanter';
import { 
  getGlobalChanter,
  setGlobalChanter,
  getComponentPreferences,
  applyComponentPreferences
} from '../utils/globalSettings';
import { 
  DEFAULT_GAIN,
  GLOBAL_GAIN_KEY
} from '../utils/constants';
import { 
  showChanterRecommendationIfNeeded
} from '../utils/CommonDialogs';

// Define storage key for component-specific preferences
const FLASHCARD_PREFS_KEY = 'flashcard_preferences';

// Define standard frequencies
const STANDARD_NOTES = {
  'Low G': 426,
  'Low A': 480,
  B: 540,
  C: 600,
  D: 640,
  E: 720,
  F: 800,
  'High G': 840,
  'High A': 960,
};

// Add this helper for logging
function logMicrophoneSelection(context, deviceId, label, source) {
  console.log(`[FlashCard] Microphone selected (${context}): deviceId=${deviceId}, label=${label}, source=${source}`);
}

const FlashCard = () => {
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [showFingering, setShowFingering] = useState(true);
  const [noteDuration, setNoteDuration] = useState(1);
  const [exerciseDuration, setExerciseDuration] = useState(30);
  const [isExerciseRunning, setIsExerciseRunning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [exerciseMode, setExerciseMode] = useState('scale');

  // New state for chanter selection
  const [chanters, setChanters] = useState([]);
  const [selectedChanter, setSelectedChanter] = useState(null); // Initialize as null instead of 'Standard'
  const [currentNotes, setCurrentNotes] = useState(STANDARD_NOTES);
  const [isInitialized, setIsInitialized] = useState(false); // Add initialization tracking

  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const noteTimeoutRef = useRef(null);
  const songPositionRef = useRef(0);
  const activeOscillatorRef = useRef(null);
  const currentNoteRef = useRef(0);

  const notes = useMemo(
    () => [
      { name: 'Low G', frequency: currentNotes['Low G'] || 426 },
      { name: 'Low A', frequency: currentNotes['Low A'] || 480 },
      { name: 'B', frequency: currentNotes['B'] || 540 },
      { name: 'C', frequency: currentNotes['C'] || 600 },
      { name: 'D', frequency: currentNotes['D'] || 640 },
      { name: 'E', frequency: currentNotes['E'] || 720 },
      { name: 'F', frequency: currentNotes['F'] || 800 },
      { name: 'High G', frequency: currentNotes['High G'] || 840 },
      { name: 'High A', frequency: currentNotes['High A'] || 960 },
    ],
    [currentNotes]
  );

  // Load chanters and preferences from localStorage and global settings
  useEffect(() => {
    const savedChanters = getItem('bagpipe_chanters', []);
    // Get component-specific preferences
    const prefs = getComponentPreferences('flashcard', {});
    
    setChanters(savedChanters);

    // First try to get the global chanter
    const globalChanter = getGlobalChanter();
    
    // Set the selected chanter from global settings, or preferences, or default to 'Standard'
    if (globalChanter && 
        (globalChanter === 'Standard' || 
         globalChanter === 'Keyboard' || 
         savedChanters.some(c => c.name === globalChanter))) {
      setSelectedChanter(globalChanter);
    }
    // Fall back to component preferences
    else if (
      prefs.selectedChanter &&
      (prefs.selectedChanter === 'Standard' || 
       prefs.selectedChanter === 'Keyboard' ||
       savedChanters.some(c => c.name === prefs.selectedChanter))
    ) {
      setSelectedChanter(prefs.selectedChanter);
      
      // Update global setting with component's preference
      setGlobalChanter(prefs.selectedChanter);
    } else {
      setSelectedChanter('Standard');
      
      // Update global setting with default
      setGlobalChanter('Standard');
    }
    
    // Load other component-specific preferences
    if (prefs.noteDuration !== undefined) {
      setNoteDuration(prefs.noteDuration);
    }
    if (prefs.exerciseDuration !== undefined) {
      setExerciseDuration(prefs.exerciseDuration);
    }
    if (prefs.exerciseMode !== undefined) {
      setExerciseMode(prefs.exerciseMode);
    }
    if (prefs.showFingering !== undefined) {
      setShowFingering(prefs.showFingering);
    }
    
    setIsInitialized(true);
  }, []);

  // Update notes when chanter selection changes
  useEffect(() => {
    if (!selectedChanter || !isInitialized) {
      return; // Don't process updates until initialization is complete
    }

    if (selectedChanter === 'Standard' || selectedChanter === 'Keyboard') {
      setCurrentNotes(STANDARD_NOTES);
    } else {
      const chanter = chanters.find(c => c.name === selectedChanter);

      if (chanter && chanter.frequencies) {
        const chanterNotes = {};
        Object.entries(STANDARD_NOTES).forEach(([note], index) => {
          chanterNotes[note] = chanter.frequencies[index] || STANDARD_NOTES[note];
        });
        setCurrentNotes(chanterNotes);
      } else {
        // If chanter not found or has no frequencies, fall back to standard notes
        setCurrentNotes(STANDARD_NOTES);
      }
    }

    // Only save preferences if initialization is complete
    if (isInitialized) {
      // Save to component-specific preferences
      saveComponentPreferences();
    }
  }, [selectedChanter, chanters, isInitialized]);
  
  // Save component-specific preferences
  const saveComponentPreferences = useCallback(() => {
    if (!isInitialized) return;
    
    applyComponentPreferences('flashcard', {
      selectedChanter,
      noteDuration,
      exerciseDuration,
      exerciseMode,
      showFingering
    });
  }, [isInitialized, selectedChanter, noteDuration, exerciseDuration, exerciseMode, showFingering]);
  
  // Save settings when they change
  useEffect(() => {
    if (!isInitialized) return;
    
    saveComponentPreferences();
  }, [noteDuration, exerciseDuration, exerciseMode, showFingering, saveComponentPreferences, isInitialized]);

  const handleChanterChange = event => {
    const newChanter = event.target.value;
    setSelectedChanter(newChanter);
    
    // Immediately update the global setting
    setGlobalChanter(newChanter);
  };

  const songSequence = useMemo(
    () => [
      1,
      4,
      6,
      6,
      5,
      4,
      2,
      1, // Low A, D, F, F, E, D, B, Low A
      1,
      4,
      6,
      6,
      5,
      8,
      6,
      8,
      6,
      5,
      6,
      5,
      4,
      2,
      1, // Low A, D, F, F, E, High A, F, High A, F, E, F, E, D, B, Low A
      1,
      4,
      6,
      6,
      5,
      4, // Low A, D, F, F, E, D
    ],
    []
  );

  const playNote = useCallback(
    noteIndex => {
      try {
        // Reuse existing AudioContext if possible
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const frequency = notes[noteIndex].frequency;
        const note = notes[noteIndex].name;

        // If using keyboard chanter, just play the note without simulation
        if (selectedChanter === 'Keyboard') {
          if (!keyboardChanter.isKeyboardActive()) {
            keyboardChanter.start();
          }
          return () => {};
        }

        // Clean up any previous oscillator
        if (activeOscillatorRef.current) {
          try {
            activeOscillatorRef.current.oscillator.stop();
            activeOscillatorRef.current.gainNode.disconnect();
            activeOscillatorRef.current.oscillator.disconnect();
          } catch (error) {
            console.error('Error cleaning up previous oscillator:', error);
          }
        }

        // Original sine wave playback for other chanters
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();

        // Use global gain value instead of hardcoded 0.5
        const gainValue = parseFloat(getItem(GLOBAL_GAIN_KEY, DEFAULT_GAIN));
        gainNode.gain.setValueAtTime(gainValue, audioContextRef.current.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.5, audioContextRef.current.currentTime + 0.1);
        gainNode.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + (noteDuration - 0.1));

        // Connect audio nodes
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);

        // Set oscillator properties
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);

        // Store current oscillator and gain node for cleanup
        activeOscillatorRef.current = { oscillator, gainNode };

        // Start the oscillator
        oscillator.start();
        oscillator.stop(audioContextRef.current.currentTime + noteDuration);

        // Return cleanup function that doesn't close the context during exercise
        return () => {
          if (!isExerciseRunning) {
            setTimeout(() => {
              if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
                audioContextRef.current = null;
              }
            }, noteDuration * 1000);
          }
        };
      } catch (error) {
        console.error('Error playing note:', error);
        trackError('audio', 'Failed to play note', 'FlashCard');
        return () => {};
      }
    },
    [noteDuration, notes, selectedChanter, isExerciseRunning]
  );

  const getNextNoteIndex = useCallback(
    (currentIndex, mode) => {
      if (mode === 'random') {
        // Create array of all possible indices except the current one
        const possibleIndices = Array.from(Array(notes.length).keys()).filter(
          index => index !== currentIndex
        );

        // Get random index from the filtered array
        const randomIndex = Math.floor(Math.random() * possibleIndices.length);
        return possibleIndices[randomIndex];
      } else if (mode === 'scale') {
        // Keep track of direction in a ref
        if (!noteTimeoutRef.current) {
          noteTimeoutRef.current = { goingUp: true };
        }

        if (noteTimeoutRef.current.goingUp) {
          // Going up the scale
          if (currentIndex === notes.length - 1) {
            // At the top, start going down
            noteTimeoutRef.current.goingUp = false;
            return currentIndex - 1;
          }
          return currentIndex + 1;
        } else {
          // Going down the scale
          if (currentIndex === 0) {
            // At the bottom, start going up
            noteTimeoutRef.current.goingUp = true;
            return currentIndex + 1;
          }
          return currentIndex - 1;
        }
      } else if (mode === 'song') {
        // Simply return the next note in the sequence
        return songSequence[songPositionRef.current];
      }
      return currentIndex;
    },
    [notes, songSequence]
  );

  const nextNote = () => {
    if (exerciseMode === 'song' && isExerciseRunning) {
      songPositionRef.current = (songPositionRef.current + 1) % songSequence.length;
    }
    setCurrentNoteIndex(prevIndex => (prevIndex + 1) % notes.length);
    trackFeatureUsage('flashcard', 'next_note');
  };

  const previousNote = () => {
    if (exerciseMode === 'song' && isExerciseRunning) {
      songPositionRef.current =
        songPositionRef.current === 0 ? songSequence.length - 1 : songPositionRef.current - 1;
    }
    setCurrentNoteIndex(prevIndex => (prevIndex === 0 ? notes.length - 1 : prevIndex - 1));
    trackFeatureUsage('flashcard', 'previous_note');
  };

  const handleDurationChange = e => {
    const value = parseInt(e.target.value) || 1;
    setNoteDuration(Math.max(1, Math.min(10, value))); // Limit between 1 and 10 seconds
  };

  const handleExerciseDurationChange = e => {
    const value = e.target.value;
    // Allow any numeric input, including partial numbers
    if (value === '' || !isNaN(value)) {
      setExerciseDuration(value);
    }
  };

  // Validate on blur to ensure final value is within bounds
  const handleExerciseDurationBlur = () => {
    const numValue = parseInt(exerciseDuration) || 0;
    if (numValue < 10) {
      setExerciseDuration(10);
    } else if (numValue > 300) {
      setExerciseDuration(300);
    } else {
      setExerciseDuration(numValue);
    }
  };

  useEffect(() => {
    let noteTimeout;

    const getNextNote = currentIndex => {
      if (exerciseMode === 'song') {
        const nextNote = songSequence[songPositionRef.current];
        songPositionRef.current = (songPositionRef.current + 1) % songSequence.length;
        return nextNote;
      } else {
        return getNextNoteIndex(currentIndex, exerciseMode);
      }
    };

    const playNextNote = () => {
      try {
        const nextIndex = getNextNote(currentNoteRef.current);
        currentNoteRef.current = nextIndex;
        setCurrentNoteIndex(nextIndex);
        playNote(nextIndex);
      } catch (error) {
        console.error('Error in playNextNote:', error);
        trackError('audio', 'Failed to play next note', 'FlashCard');
      }
    };

    if (isExerciseRunning) {
      // Ensure we have a fresh AudioContext when starting exercise
      if (audioContextRef.current && audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Start the countdown timer
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            endExercise();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Play first note immediately
      const firstIndex =
        exerciseMode === 'scale'
          ? 0
          : exerciseMode === 'song'
            ? songSequence[0]
            : getNextNoteIndex(currentNoteRef.current, 'random');
      currentNoteRef.current = firstIndex;
      setCurrentNoteIndex(firstIndex);
      
      try {
        playNote(firstIndex);
        // Schedule next note with a slight delay to ensure proper timing
        noteTimeout = setInterval(playNextNote, noteDuration * 1000);
      } catch (error) {
        console.error('Error starting exercise playback:', error);
        trackError('audio', 'Failed to start exercise playback', 'FlashCard');
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (noteTimeout) {
        clearInterval(noteTimeout);
      }
      // Don't close AudioContext here - let it be managed by playNote
    };
  }, [isExerciseRunning, noteDuration, exerciseMode, songSequence, getNextNoteIndex, playNote]);

  const startExercise = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    setIsExerciseRunning(true);
    setTimeRemaining(exerciseDuration);
    songPositionRef.current = 0;
    currentNoteRef.current = currentNoteIndex; // Initialize ref with current state

    // Start with appropriate note based on mode
    const firstIndex =
      exerciseMode === 'scale'
        ? 0
        : exerciseMode === 'song'
          ? songSequence[0]
          : getNextNoteIndex(currentNoteRef.current, 'random');
    currentNoteRef.current = firstIndex;
    setCurrentNoteIndex(firstIndex);
    setTimeout(() => playNote(firstIndex), 100); // Small delay to ensure state is updated
    trackFeatureUsage('flashcard_exercise', 'start');
  };

  const endExercise = () => {
    setIsExerciseRunning(false);
    setTimeRemaining(0);
    songPositionRef.current = 0;

    // Clean up any playing note
    if (activeOscillatorRef.current) {
      try {
        activeOscillatorRef.current.oscillator.stop();
        activeOscillatorRef.current.gainNode.disconnect();
        activeOscillatorRef.current.oscillator.disconnect();
      } catch (error) {
        console.error('Error cleaning up oscillator:', error);
      }
      activeOscillatorRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Reset scale direction
    noteTimeoutRef.current = null;
    trackFeatureUsage('flashcard_exercise', 'end');
  };

  const playCurrentNote = () => {
    try {
      playNote(currentNoteIndex);
      trackFeatureUsage('flashcard', 'play_note');
    } catch (error) {
      console.error('Error playing note:', error);
      trackError('audio', 'Failed to play note', 'FlashCard');
    }
  };

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Track component mount
  useEffect(() => {
    trackFeatureUsage('flashcard', 'open');
    return () => {
      trackFeatureUsage('flashcard', 'close');
    };
  }, []);

  // Create menus for the SubMenuBar
  const menus = useMemo(() => {
    // Create exercise mode menu items
    const exerciseModeItems = ['Random', 'Scale', 'Song'];

    // Create note duration items
    const noteDurationItems = ['0.5 Second', '1 Second', '2 Seconds'];

    // Create exercise duration items
    const exerciseDurationItems = ['15 Seconds', '30 Seconds', '1 Minute', '2 Minutes'];

    // Create chanter menu items including Standard, Keyboard, and all calibrated chanters
    const chanterItems = ['Standard', 'Keyboard', ...chanters.map(chanter => chanter.name)];

    // Create display menu items
    const displayItems = ['Show Fingering', 'Hide Fingering'];

    return {
      Mode: {
        label: 'Mode',
        items: exerciseModeItems,
        selected:
          exerciseMode === 'random' ? 'Random' : exerciseMode === 'scale' ? 'Scale' : 'Song',
        onSelect: item => {
          setExerciseMode(item.toLowerCase());
          trackFeatureUsage('flashcard_exercise_mode', item);
        },
      },
      Chanter: {
        label: 'Chanter',
        items: chanterItems,
        selected: selectedChanter,
        onSelect: item => {
          setSelectedChanter(item);
          // Immediately update global setting
          setGlobalChanter(item);
          trackFeatureUsage('flashcard_chanter', item);
        },
      },
      Display: {
        label: 'Display',
        items: displayItems,
        selected: showFingering ? 'Show Fingering' : 'Hide Fingering',
        onSelect: item => {
          setShowFingering(item === 'Show Fingering');
        },
      },
    };
  }, [exerciseMode, noteDuration, exerciseDuration, chanters, selectedChanter, showFingering]);

  // Show dialog only once after initialization is complete
  useEffect(() => {
    // Only run the effect if initialization is complete
    if (!isInitialized) return;
    
    // Check if user has a custom chanter selected
    // Force dialog to show for Standard/Keyboard chanters
    const forceForStandard = selectedChanter === 'Standard' || selectedChanter === 'Keyboard';
    showChanterRecommendationIfNeeded(selectedChanter, chanters, 'flashcard', forceForStandard);
  }, [isInitialized, selectedChanter, chanters]);

  return (
    <div className="app-flashcard flashcard-theme">
      <div className="app-flashcard__card-container">
        <SubMenuBar menus={menus} />
        <div className="app-flashcard__center-container">
          <div className="app-flashcard__left-container">
            <div className="app-flashcard__note-display">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <button
                  className="app-flashcard__nav-button"
                  onClick={previousNote}
                  disabled={isExerciseRunning}
                  style={{ marginRight: '8px' }}
                >
                  ←
                </button>
                <div className="app-flashcard__note-staff-container">
                  <NoteStaff noteName={notes[currentNoteIndex].name} />
                </div>
                <button
                  className="app-flashcard__nav-button"
                  onClick={nextNote}
                  disabled={isExerciseRunning}
                  style={{ marginLeft: '8px' }}
                >
                  →
                </button>
              </div>
              <h3>{notes[currentNoteIndex].name}</h3>
              <div className="app-flashcard__note-frequency">Frequency: {notes[currentNoteIndex].frequency} Hz</div>
              <button
                className="app-flashcard__play-button"
                onClick={playCurrentNote}
                disabled={isExerciseRunning}
              >
                Play Note
              </button>
            </div>
            <div className="app-flashcard__progress">
              {isExerciseRunning ? (
                <div className="app-flashcard__timer">Time Remaining: {formatTime(timeRemaining)}</div>
              ) : (
                <div>
                  Note {currentNoteIndex + 1} of {notes.length}
                </div>
              )}
            </div>
            <div className="app-flashcard__controls">
              <div className="app-flashcard__timing-settings">
                <h4>Duration (seconds)</h4>
                <table className="app-flashcard__duration-table">
                  <tbody>
                    <tr>
                      <td>
                        <label htmlFor="noteDuration">Note:</label>
                      </td>
                      <td>
                        <input
                          type="number"
                          id="noteDuration"
                          min="1"
                          max="10"
                          value={noteDuration}
                          onChange={handleDurationChange}
                          disabled={isExerciseRunning}
                        />
                      </td>
                      <td>
                        <label htmlFor="exerciseDuration">Exercise:</label>
                      </td>
                      <td>
                        <input
                          type="number"
                          id="exerciseDuration"
                          min="10"
                          max="300"
                          value={exerciseDuration}
                          onChange={handleExerciseDurationChange}
                          onBlur={handleExerciseDurationBlur}
                          disabled={isExerciseRunning}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="app-flashcard__exercise-controls">
              <button
                className="app-flashcard__exercise-button"
                onClick={startExercise}
                disabled={isExerciseRunning}
              >
                Start Exercise
              </button>
              <button
                className="app-flashcard__exercise-button"
                onClick={endExercise}
                disabled={!isExerciseRunning}
              >
                End Exercise
              </button>
            </div>
          </div>
          {showFingering && (
            <div className="app-flashcard__fingering-display">
              <ChanterSvg width="100%" height="100%" activeNote={notes[currentNoteIndex].name} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlashCard;
